import { useRuntimeConfig } from '#imports'
import { type Component, defineAsyncComponent, hydrateOnVisible } from 'vue'

import type { BlockType } from '@backmarket/http-api/src/api-specs-content/models/block'

/**
 * Eagerly loaded components to be used to avoid nested lazy hydration
 * See ModularCardSection.vue that uses <BlockRenderer hydration-mode="eager" />
 */
const COMPONENT_FROM_CONTENT_TYPE: Record<BlockType, Component> = {
  'Article-author': defineAsyncComponent(
    () => import('../../blocks/Author/Author.vue'),
  ),
  AnimatedProductCheckpoints: defineAsyncComponent(
    () =>
      import('../../blocks/AnimatedProductCheckpoints/AnimatedProductCheckpoints.vue'),
  ),
  Button: defineAsyncComponent(() => import('../../blocks/Button/Button.vue')),
  Buttons: defineAsyncComponent(
    () => import('../../blocks/Buttons/Buttons.vue'),
  ),
  ArticleCardsList: defineAsyncComponent(
    () => import('../../blocks/ArticleCardsList/ArticleCardsList.vue'),
  ),
  // NB: the contentType id is fixed to "Carousel" but the contentType has been renamed "Slider" in the CMS
  Carousel: defineAsyncComponent(
    () => import('../../blocks/Slider/Slider.vue'),
  ),
  Categories: defineAsyncComponent(
    () => import('../../blocks/Categories/Categories.vue'),
  ),
  CategoryTree: defineAsyncComponent(
    () => import('../../blocks/CategoryTree/CategoryTree.vue'),
  ),
  Content: defineAsyncComponent(
    () => import('../../blocks/Content/Content.vue'),
  ),
  ContentMedia: defineAsyncComponent(
    () => import('../../blocks/ContentMedia/ContentMedia.vue'),
  ),
  DiscountBanner: defineAsyncComponent(
    () => import('../../blocks/DiscountBanner/DiscountBanner.vue'),
  ),
  EmailCollector: defineAsyncComponent(
    () => import('../../blocks/EmailCollector/EmailCollector.vue'),
  ),
  EmbeddedYoutubeVideo: defineAsyncComponent(
    () => import('../../blocks/EmbeddedYoutubeVideo/EmbeddedYoutubeVideo.vue'),
  ),
  EnhancedCarousel: defineAsyncComponent(
    () => import('../../blocks/EnhancedCarousel/EnhancedCarousel.vue'),
  ),
  Faq: defineAsyncComponent(() => import('../../blocks/Faq/Faq.vue')),
  ListItem: defineAsyncComponent(
    () => import('../../blocks/ListItem/ListItem.vue'),
  ),
  Media: defineAsyncComponent(() => import('../../blocks/Media/Media.vue')),
  ModularCardsCarousel: defineAsyncComponent(
    () => import('../../blocks/ModularCard/ModularCardsCollection.vue'),
  ),
  ModularCardImage: defineAsyncComponent(
    () => import('../../blocks/ModularCard/ModularCardImage.vue'),
  ),
  PersonalizedFeed: defineAsyncComponent(
    () => import('../../blocks/PersonalizedFeed/PersonalizedFeed.vue'),
  ),
  PressCarousel: defineAsyncComponent(
    () => import('../../blocks/PressCarousel/PressCarousel.vue'),
  ),
  ProductGrades: defineAsyncComponent(
    () => import('../../blocks/ProductGrades/ProductGrades.vue'),
  ),
  ProductSpotlight: defineAsyncComponent(
    () => import('../../blocks/ProductSpotlight/ProductSpotlight.vue'),
  ),
  Products: defineAsyncComponent(
    () => import('../../blocks/Products/Products.vue'),
  ),
  UpcomingSaleCountdownBanner: defineAsyncComponent(
    () => import('../../blocks/CountdownBanner/CountdownBanner.vue'),
  ),
  Quiz: defineAsyncComponent(() => import('../../blocks/Quiz/Quiz.vue')),
  RefinedProducts: defineAsyncComponent(
    () => import('../../blocks/RefinedProducts/RefinedProducts.vue'),
  ),
  Reinsurance: defineAsyncComponent(
    () => import('../../blocks/Reassurance/Reassurance.vue'),
  ),
  Reviews: defineAsyncComponent(
    () => import('../../blocks/Reviews/Reviews.vue'),
  ),
  RelatedLinks: defineAsyncComponent(
    () => import('../../blocks/RelatedLinks/RelatedLinks.vue'),
  ),
  RichText: defineAsyncComponent(
    () => import('../../blocks/RichText/RichText.vue'),
  ),
  SectionBanner: defineAsyncComponent(
    () => import('../../blocks/SectionBanner/SectionBanner.vue'),
  ),
  SocialMediaEmbed: defineAsyncComponent(
    () => import('../../blocks/SocialMediaEmbed/SocialMediaEmbed.vue'),
  ),
  TrustpilotWidget: defineAsyncComponent(
    () => import('../../blocks/TrustpilotWidget/TrustpilotWidget.vue'),
  ),
  VideosBlock: defineAsyncComponent(
    () => import('../../blocks/VideosBlock/VideosBlock.vue'),
  ),
  TradeInSearch: defineAsyncComponent(
    () => import('../../blocks/TradeInSearch/TradeInSearch.vue'),
  ),
  TradeInFooterLinks: defineAsyncComponent(
    () => import('../../blocks/TradeInFooterLinks/TradeInFooterLinks.vue'),
  ),
}
const LAZY_HYDRATED_COMPONENT_FROM_CONTENT_TYPE: Record<BlockType, Component> =
  {
    'Article-author': defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Author/Author.vue'),
    }),
    AnimatedProductCheckpoints: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/AnimatedProductCheckpoints/AnimatedProductCheckpoints.vue'),
    }),
    Button: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Button/Button.vue'),
    }),
    Buttons: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Buttons/Buttons.vue'),
    }),
    ArticleCardsList: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/ArticleCardsList/ArticleCardsList.vue'),
    }),
    // NB: the contentType id is fixed to "Carousel" but the contentType has been renamed "Slider" in the CMS
    Carousel: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Slider/Slider.vue'),
    }),
    Categories: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Categories/Categories.vue'),
    }),
    CategoryTree: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/CategoryTree/CategoryTree.vue'),
    }),
    Content: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Content/Content.vue'),
    }),
    ContentMedia: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/ContentMedia/ContentMedia.vue'),
    }),
    DiscountBanner: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/DiscountBanner/DiscountBanner.vue'),
    }),
    EmailCollector: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/EmailCollector/EmailCollector.vue'),
    }),
    EmbeddedYoutubeVideo: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/EmbeddedYoutubeVideo/EmbeddedYoutubeVideo.vue'),
    }),
    EnhancedCarousel: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/EnhancedCarousel/EnhancedCarousel.vue'),
    }),
    Faq: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Faq/Faq.vue'),
    }),
    ListItem: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/ListItem/ListItem.vue'),
    }),
    Media: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Media/Media.vue'),
    }),
    ModularCardsCarousel: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/ModularCard/ModularCardsCollection.vue'),
    }),
    ModularCardImage: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/ModularCard/ModularCardImage.vue'),
    }),
    PersonalizedFeed: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/PersonalizedFeed/PersonalizedFeed.vue'),
    }),
    PressCarousel: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/PressCarousel/PressCarousel.vue'),
    }),
    ProductGrades: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/ProductGrades/ProductGrades.vue'),
    }),
    ProductSpotlight: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/ProductSpotlight/ProductSpotlight.vue'),
    }),
    Products: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Products/Products.vue'),
    }),
    UpcomingSaleCountdownBanner: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/CountdownBanner/CountdownBanner.vue'),
    }),
    Quiz: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Quiz/Quiz.vue'),
    }),
    RefinedProducts: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/RefinedProducts/RefinedProducts.vue'),
    }),
    Reinsurance: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Reassurance/Reassurance.vue'),
    }),
    Reviews: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/Reviews/Reviews.vue'),
    }),
    RelatedLinks: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/RelatedLinks/RelatedLinks.vue'),
    }),
    RichText: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/RichText/RichText.vue'),
    }),
    SectionBanner: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/SectionBanner/SectionBanner.vue'),
    }),
    SocialMediaEmbed: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/SocialMediaEmbed/SocialMediaEmbed.vue'),
    }),
    TrustpilotWidget: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/TrustpilotWidget/TrustpilotWidget.vue'),
    }),
    VideosBlock: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/VideosBlock/VideosBlock.vue'),
    }),
    TradeInFooterLinks: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () =>
        import('../../blocks/TradeInFooterLinks/TradeInFooterLinks.vue'),
    }),
    TradeInSearch: defineAsyncComponent({
      hydrate: hydrateOnVisible(),
      loader: () => import('../../blocks/TradeInSearch/TradeInSearch.vue'),
    }),
  }

export type HydrationMode = 'eager' | 'lazy' | 'auto'

/**
 *
 * @param hydrationMode eager|lazy|auto
 * Make sure to pass hydrationMode: "eager" to not use lazy hydration when using BlockRenderer inside a CMS block. Nested lazy hydration may trigger bugs (e.g. "Cannot destructure property 'type' of 'xx'" ) and make the page unresponsive.
 * See ModularCardSection.vue that uses <BlockRenderer hydration-mode="eager" /> to avoid nested lazy hydration.
 * - 'eager' : all blocks are hydrated on page load
 * - 'lazy' : all blocks are hydrated when they are visible
 * - 'auto' : blocks are lazily hydrated by default (unless if kill switch KILL_CMS_LAZY_HYDRATION is enabled)
 * @returns
 */
export function useBlockRenderer(hydrationMode: HydrationMode = 'auto'): {
  getBlock: (type: BlockType) => Component
  blockExists: (type: BlockType) => boolean
} {
  const { KILL_CMS_LAZY_HYDRATION } = useRuntimeConfig().public

  const lazyHydrationEnabled = (() => {
    if (KILL_CMS_LAZY_HYDRATION) return false

    if (hydrationMode === 'eager') return false

    return true
  })()

  if (lazyHydrationEnabled)
    return {
      getBlock: (type) => {
        return LAZY_HYDRATED_COMPONENT_FROM_CONTENT_TYPE[type]
      },
      blockExists: (type) => type in LAZY_HYDRATED_COMPONENT_FROM_CONTENT_TYPE,
    }

  return {
    getBlock: (type) => {
      return COMPONENT_FROM_CONTENT_TYPE[type]
    },
    blockExists: (type) => type in COMPONENT_FROM_CONTENT_TYPE,
  }
}
